import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { ModalType, useFormikGoodFilters } from '../../../../utils';
import { GoodsFilter } from '../../types';
import {
  useEntryStates,
  useRefundStates,
} from '../../../../share';
import WarehouseManageContainer from './WarehouseManageContainer';
import WarehouseDetailContainer from './WarehouseDetailContainer';
import { useCancelGoods, useCancellationRefund, useFetchWarehouseRequests } from '../../../../hooks';
import WarehouseView from '../Views/WarehouseView';
import { useCustomToast } from '../../../../components';
import RefundsDetailContainer from '../../../Refunds/components/Containers/RefundsDetailContainer';
import EntryRequestDetailContainer from '../../../EntryRequests/components/Containers/EntryRequestDetailContainer';
import { useAtom } from 'jotai';
import { authAtom } from '../../../../state';
import { useRefundAnnul } from '../../../../utils/validators/refund-request/refund-request-validator';

const handleModalToggle = (
  modalState: { type: ModalType; id?: string },
  setModalState: React.Dispatch<
    React.SetStateAction<{ type: ModalType; id?: string }>
  >,
  type: ModalType,
  id?: string
) => {
  setModalState((prev) => ({
    type: prev.type === type ? null : type,
    id: id,
  }));
};

const GoodsContainer: React.FC = () => {
  const { data: entryStates } = useEntryStates();
  const { data: refundStates } = useRefundStates();

  const [generalStates, setGeneralStates] = React.useState<any>();
  const [modalState, setModalState] = React.useState<{
    type: ModalType;
    id?: string;
  }>({ type: null, id: undefined });

  const { mutateAsync: cancelRequest } = useCancelGoods();
  const { mutateAsync: cancelRefunds } = useCancellationRefund();

  const [authData] = useAtom(authAtom);
  
  const petitionable_type = !authData?.superadmin
    ? [
        authData?.portalPermissions.includes('webportal-consumables') && 'GoodPetition',
        authData?.portalPermissions.includes('webportal-entry') && 'EntryPetition',
        authData?.portalPermissions.includes('webportal-refund') && 'RefundPetition'
      ].filter(Boolean).join(',')
    : '';


  const [filter, setFilter] = React.useState<GoodsFilter>({
    petitionable_type
  });

  const {
    data: goods,
    isPending: loadList,
    refetch,
  } = useFetchWarehouseRequests(filter);
  const [itemSelected, setItemSelected] = useState<any>();
  const [loadingData, setLoadingData] = useState<boolean>(true);

  const { showToast } = useCustomToast();

  const handleFilter = useCallback(
    async (
      values: Partial<GoodsFilter>,
      setFilter: React.Dispatch<React.SetStateAction<GoodsFilter>>
    ) => {
      setFilter({
        ...values,
        petitionable_type: values?.petitionable_type
          ? values.petitionable_type
          : petitionable_type,
        created_at: values.created_at
          ? new Date(values.created_at).toISOString().split('T')[0]
          : '',
      });
    },
    []
  );
  const formik = useFormikGoodFilters({
    onSubmit: (values) => handleFilter(values, setFilter),
  });

  const onCancelRequest = async (id: string, observations: string, isRefund: boolean) => {
    try {
      if (isRefund) {
        await cancelRefunds({ id, observations });
      } else {
        await cancelRequest({ id, data: { observations } });
      }
      handleFilter({}, setFilter);
    } catch (error) {
      showToast('error', 'Error al anular solicitud.');
    }
  };

  const handlePagination = (page: number) => {
    setFilter({
      page: page.toString(),
      status_id: formik.values.status_id || '',
    });
  };

  useEffect(() => {
    if (
      refundStates &&
      refundStates?.length > 0 &&
      entryStates &&
      entryStates?.length > 0
    ) {
      const statesCombinado = [...refundStates, ...entryStates];
      const sinDuplicados = Array.from(
        new Map(statesCombinado.map((obj) => [obj.id, obj])).values()
      );
      setGeneralStates(sinDuplicados);
    }
  }, [refundStates, entryStates]);

  useEffect(() => {
    refetch();
  }, [filter, refetch]);

  return (
    <Fragment>
      {/* Bienes */}
      {modalState.type === 'manage' && (
        <WarehouseManageContainer
          isOpen={modalState.type === 'manage'}
          OnClose={() => {
            handleModalToggle(modalState, setModalState, 'manage');
            formik.resetForm();
          }}
          isLoading={loadList}
          refetch={refetch}
          isApproveRequest={false}
          id={modalState.id}
        />
      )}
      {modalState.type === 'detail' && (
        <WarehouseDetailContainer
          isOpen={modalState.type === 'detail'}
          OnClose={() => {
            handleModalToggle(modalState, setModalState, 'detail');
            formik.resetForm();
          }}
          isLoading={loadList}
          id={modalState.id}
        />
      )}

      {/* Reintegros */}
      {modalState.type === 'manage-refound' && (
        <RefundsDetailContainer
          isOpen={modalState.type === 'manage-refound'}
          OnClose={() =>
            handleModalToggle(modalState, setModalState, 'manage-refound')
          }
          isLoading={loadList}
          gestorFromWarehouse={
            itemSelected && itemSelected.can_manage
          }
          refetch={refetch}
          id={modalState.id}
        />
      )}
      {modalState.type === 'detail-refound' && (
        <RefundsDetailContainer
          isOpen={modalState.type === 'detail-refound'}
          OnClose={() =>
            handleModalToggle(modalState, setModalState, 'detail-refound')
          }
          isLoading={loadList}
          id={modalState.id}
        />
      )}

      {/* Entradas */}
      {modalState.type === 'detail-entry' && (
        <EntryRequestDetailContainer
          isOpen={modalState.type === 'detail-entry'}
          OnClose={() =>
            handleModalToggle(modalState, setModalState, 'detail-entry')
          }
          isLoading={loadList}
          refetch={refetch}
          isApproveRequest={false}
          id={modalState.id}
        />
      )}

      <WarehouseView
        isLoading={loadList}
        data={goods}
        states={generalStates}
        formik={formik}
        onViewManage={(item: any) => {
          setModalState({
            type: 'manage',
            id: item.id,
          });
          setItemSelected(item);
        }}
        onViewDetail={(item: any) => {
          setModalState({
            type: 'detail',
            id: item.id,
          });
          setItemSelected(item);
        }}
        onViewManageRefund={(item: any) => {
          setModalState({
            type: 'manage-refound',
            id: item.id,
          });
          setItemSelected(item);
        }}
        onViewDetailRefund={(item: any) => {
          setModalState({
            type: 'detail-refound',
            id: item.id,
          });
          setItemSelected(item);
        }}
        onViewDetailEntry={(item: any) => {
          setModalState({
            type: 'detail-entry',
            id: item.id,
          });
          setItemSelected(item);
        }}
        onPagination={(page) => {handlePagination(page)}}
        onResetFilters={() => {
          handleFilter({}, setFilter);
          formik.resetForm();
        }}
        onCancelRequest={(id, observations, isRefund) =>
          onCancelRequest(id, observations, isRefund)
        }
        loadingData={loadingData}
      />
    </Fragment>
  );
};

export default GoodsContainer;
